import { useCallback, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  CSVoucherCreateFormSchemas,
  CSVoucherCreateFormTypes,
  VoucherCreateFormTypes,
} from 'schemas/campaign-voucher-schemas'
import { useVoucherCreate } from './use-voucher-create'
import {
  useVoucherMerchantsSelectQuery,
  useVoucherMembersSelectQuery,
} from './use-voucher-merchants-lookup'
import { getMembers } from 'api/members'
import { MemberData } from 'types/member'
import { MerchantDataTypes } from 'types/merchant'
import { getVouchersTemplateAPI } from 'api/campaign-voucher'
import { useVouchersSelectQuery } from './use-voucher-create'

import { VOUCHER_TYPES_DROPDOWN, MERCHANT_SELECTION_TYPES_DROPDOWN } from 'constants/filters'
import { getMerchantAPI } from 'api/merchant'
import { toast } from 'react-toastify'

import { useQueryClient } from 'react-query'
import { QUERY_KEYS } from "constants/querykeys";
import SearchBar from 'components/SearchBar/SearchBar'

export const CreateNewCSVoucherForm = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const [selectedVoucher, setSelectedVoucher] = useState<{ label: string; value: string } | null>(null);
  const [inputValue, setInputValue] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [voucherTemplates, setVoucherTemplates] = useState<any[]>([])
  const { selectedMerchants, onSelectMerchants } = useVoucherMerchantsSelectQuery()
  const { selectedMembers, onSelectMembers } = useVoucherMembersSelectQuery()
  const { voucherOptions } = useVouchersSelectQuery()
  const [filteredOptions, setFilteredOptions] = useState(voucherOptions)
  const [memberLimitError, setMemberLimitError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // fetch templates on mount and keep them in state
  useEffect(() => {
    let mounted = true
    async function fetchTemplates() {
      setLoading(true)
      try {
        const response = await getVouchersTemplateAPI()
        const templates = response.data || []
        if (!mounted) return
        setVoucherTemplates(templates)
        // if user already selected a voucher (unlikely on mount), populate it
        if (selectedVoucher) {
          const tpl = templates.find(t => String(t.voucherName) === String(selectedVoucher.value))
          if (tpl) populateFormFromTemplate(tpl)
        }
      } finally {
        setLoading(false)
      }
    }
    fetchTemplates()
    return () => { mounted = false }
    // eslint-disable-next-line
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const populateFormFromTemplate = (voucher: any) => {
    const sku = voucher.sku && voucher.sku !== '' ? [voucher.sku] : []
    const duration = Number(voucher.voucherDuration) || 0
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const formatDateTime = (date: Date, endOfDay = false) => {
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const time = endOfDay ? '23:59:59' : '00:00:00'
      return `${yyyy}-${mm}-${dd} ${time}`
    }
    const startDate = formatDateTime(today)
    // const end = new Date(today)
    // end.setDate(end.getDate() + duration)
    // const endDate = formatDateTime(end, true)

    form.reset({
      voucherTypeId: 2, //always 2
      voucherIssuerId: voucher.voucherIssuerId,
      voucherIssuerName: voucher.voucherIssuerName ?? '',
      voucherIssuer: voucher.voucherIssuerId + ' ' + (voucher.voucherIssuerName ?? ''),
      voucherValue: voucher.voucherValue,
      pluCode: voucher.pluCode ?? '',
      voucherName: voucher.voucherName ?? '',
      startDate,
      endDate: voucher.endDate ?? '',
      references: [],
      voucherCount: null,
      merchants: null,
      flSecurityCode: null,
      description: voucher.voucherDescription ?? '',
      imageUrl: voucher.imageUrl ?? '',
      mcaId: voucher.mcaId,
      offerId: voucher.offerId ?? null,
      offerValue2: voucher.offerValue2 ?? 0,
      offerValue3: voucher.offerValue3 ?? 0,
      offerTypeId: voucher.offerTypeId ?? null,
      productSkus: sku,
      mca: voucher.mcaId + ' ' + (voucher.mcaCampaignName ?? ''),
    })
  }

  const formDefaultValues: CSVoucherCreateFormTypes = {
    voucherTypeId: 2, //always 2
    voucherIssuerId: 0,
    voucherIssuerName: '',
    voucherValue: 0,
    pluCode: null,
    voucherName: '',
    startDate: '',
    endDate: '',
    references: [],
    voucherCount: null,
    merchants: null,
    flSecurityCode: null,
    description: '',
    imageUrl: '',
    mcaId: 0,
    offerId: null,
    offerValue2: 0,
    offerValue3: 0,
    offerTypeId: null,
    productSkus: [],
    mca: '',
    voucherIssuer: ''
  }

  const form = useForm<CSVoucherCreateFormTypes>({
    resolver: zodResolver(CSVoucherCreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(voucherOptions)
    } else {
      const filtered = voucherOptions.filter(opt =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      setFilteredOptions(filtered)
    }
  }, [inputValue, voucherOptions])

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === "merchantTypeId" && values.merchantTypeId === 1) {
        form.setValue("merchants", null)
      }
    })
    return () => subscription.unsubscribe()
  }, [form])

  const CS_VOUCHER_TYPES_DROPDOWN = VOUCHER_TYPES_DROPDOWN.slice(1)

  const {
    mutate: mutateVoucherCreate,
    isSuccess: isSuccessVoucherCreate,
    isError: isErrorVoucherCreate,
    isLoading: isLoadingVoucherCreate,
  } = useVoucherCreate()

  const queryClient = useQueryClient();

  const onSubmit = useCallback(
    (data: VoucherCreateFormTypes) => {
      const payload = {
        ...data,
      }

      // console.log('Submitting payload:', payload)

      mutateVoucherCreate(
        { formData: payload },
        {
          async onSuccess() {
            toast.success('Voucher created successfully!')
            onCloseModal()

            await queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === QUERY_KEYS.VOUCHER_LIST
            })
          },
          onError: () => {
            const message = JSON.parse(JSON.stringify(isErrorVoucherCreate))
            toast.error(message)
          },
        }
      )
    },
    [mutateVoucherCreate, onCloseModal, queryClient]
  )

  const [memberResults, setMemberResults] = useState<MemberData[]>([])
  const [memberSearchError, setMemberSearchError] = useState<string | null>(null)
  const [isMemberSearching, setIsMemberSearching] = useState(false)

  const [merchantResults, setMerchantResults] = useState<MerchantDataTypes[]>([])
  const [merchantSearchError, setMerchantSearchError] = useState<string | null>(null)
  const [isMerchantSearching, setIsMerchantSearching] = useState(false)

  const voucherTypeId = form.watch("voucherTypeId")

  useEffect(() => {
    selectedMembers.forEach(onSelectMembers)
    form.setValue("references", [])
    setMemberLimitError(null)
    setMemberResults([])
    setMemberSearchError(null)
  }, [voucherTypeId])

  // const formValues = form.watch()
  // useEffect(() => {
  //   console.log("Current form values:", formValues)
  // }, [formValues])

  if (loading) return <div className="p-4">Loading...</div>

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          name="voucherName"
          control={form.control}
          render={({ field }) => (
            <Dropdown
              title="Select Voucher"
              options={filteredOptions}
              value={selectedVoucher}
              onSelect={(option: DropDownItem | null) => {
                setSelectedVoucher(option)
                field.onChange(option ? Number(option.value) : null)
                if (option) {
                  const tpl = voucherTemplates.find(t => String(t.voucherName) === String(option.value))
                  if (tpl) populateFormFromTemplate(tpl)
                }
              }}
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              containerClassName="focus:border-primary"
              placeholder="Search vouchers..."
              isClearable
              isSearchable
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="mca"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="mca"
              label="MCA"
              containerClassName="w-full"
              value={value ?? ""} //mcaId + mcaName
              disable
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherTypeId"
          render={({ field: { value } }) => (
            <Dropdown
              defaultValue={CS_VOUCHER_TYPES_DROPDOWN[0]}
              title="Voucher Type"
              options={CS_VOUCHER_TYPES_DROPDOWN}
              className="w-full"
              disabled
            />
          )}
        />
      </div>

      {/* Show Member Lookup when voucher type is selected */}
      {form.watch("voucherTypeId") ? (
        <div className="mb-4 items-center">
          <p className="mt-4 font-light tracking-wide mb-2">
            Search members to allocate the voucher
          </p>
          <Controller
            name="references"
            control={form.control}
            render={({ field }) => {
              const voucherTypeId = form.watch("voucherTypeId")
              const maxRefs = voucherTypeId === 1 ? 1 : 10
              const canAddMore = (selectedMembers.length || 0) < maxRefs

              return (
                <>
                  <SearchBar
                    isMemberLookup
                    placeholder=""
                    startLoading={() => {
                      setIsMemberSearching(true)
                      setMemberSearchError(null)
                      setMemberResults([])
                    }}
                    stopLoading={() => setIsMemberSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const members = await getMembers(
                          parsedValues.first_name || parsedValues.fname,
                          parsedValues.last_name || parsedValues.lname,
                          parsedValues.trader_id || parsedValues.id,
                          parsedValues.card_number || parsedValues.card,
                          parsedValues.email || parsedValues.email,
                          parsedValues.phone_number || parsedValues.phoneNumber
                        )
                        if (members.length === 0) {
                          setMemberSearchError("No results found")
                        } else {
                          setMemberResults(members)
                        }
                      } catch (err) {
                        setMemberSearchError("No results found")
                      } finally {
                        setIsMemberSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {memberSearchError && (
                    <div className="text-red-600 text-sm mt-1">{memberSearchError}</div>
                  )}
                  {isMemberSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {memberResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Id</th>
                            <th className="px-2 py-1 text-left">First Name</th>
                            <th className="px-2 py-1 text-left">Last Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberResults.map((m) => {
                            const isSelected = selectedMembers.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-100" : ""
                                  }`}
                                onClick={() => {
                                  const newMember: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.firstName} ${m.lastName}`,
                                  }

                                  if (isSelected) {
                                    onSelectMembers(newMember)
                                    field.onChange(
                                      (field.value || []).filter(
                                        (id: string) => id !== newMember.value
                                      )
                                    )
                                    setMemberLimitError(null)
                                  } else {
                                    if (!canAddMore) {
                                      setMemberLimitError(
                                        `You can only select up to ${maxRefs} member${maxRefs > 1 ? "s" : ""
                                        } for this voucher type.`
                                      )
                                      return
                                    }

                                    setMemberLimitError(null)
                                    onSelectMembers(newMember)
                                    const updatedRefs =
                                      voucherTypeId === 1
                                        ? [newMember.value]
                                        : [...(field.value || []), newMember.value]

                                    field.onChange(updatedRefs)
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.firstName}</td>
                                <td className="px-2 py-1">{m.lastName}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected members */}
                  {selectedMembers.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Members</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMembers.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMembers(item)
                              field.onChange(
                                (field.value || []).filter(
                                  (id: string) => id !== item.value
                                )
                              )
                              setMemberLimitError(null)
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reference limit message */}
                  {memberLimitError && (
                    <div className="text-red text-sm mt-1">{memberLimitError}</div>
                  )}
                </>
              )
            }}
          />
        </div>
      ) : null}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherIssuer"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherIssuer"
              label="Voucher Issuer"
              containerClassName="w-full"
              value={value ?? ""} //voucherIssuerId + voucherIssuerName
              disable
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="merchantTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={MERCHANT_SELECTION_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Stores to use the voucher"
              options={MERCHANT_SELECTION_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {/* Show Merchant Lookup only when type 2 is selected */}
      {form.watch("merchantTypeId") === 2 && (
        <>
          <div className="mb-4 items-center">
            <p className="mt-4 font-light tracking-wide mb-2">
              Search stores to use the voucher
            </p>
            <Controller
              name="merchants"
              control={form.control}
              render={({ field }) => (
                <>
                  <SearchBar
                    isMerchantLookup
                    placeholder="Search stores..."
                    startLoading={() => {
                      setIsMerchantSearching(true)
                      setMerchantSearchError(null)
                      setMerchantResults([])
                    }}
                    stopLoading={() => setIsMerchantSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const merchants = await getMerchantAPI(
                          parsedValues.traderId || parsedValues.id,
                          parsedValues.merchantName || parsedValues.name,
                          parsedValues.merchantCode || parsedValues.mcode
                        )
                        if (merchants.length === 0) {
                          setMerchantSearchError("No results found")
                        } else {
                          setMerchantResults(merchants)
                        }
                      } catch (err) {
                        setMerchantSearchError("No results found")
                      } finally {
                        setIsMerchantSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {merchantSearchError && (
                    <div className="text-red-600 text-sm mt-1">{merchantSearchError}</div>
                  )}
                  {isMerchantSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {merchantResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Identifier</th>
                            <th className="px-2 py-1 text-left">Merchant Name</th>
                            <th className="px-2 py-1 text-left">Merchant Code</th>
                            <th className="px-2 py-1 text-left">External Id</th>
                          </tr>
                        </thead>
                        <tbody>
                          {merchantResults.map((m) => {
                            const isSelected = selectedMerchants.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100' : ''
                                  }`}
                                onClick={() => {
                                  const newMerchant: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.merchantName} (${m.merchantCode})`,
                                  }

                                  if (!isSelected) {
                                    onSelectMerchants(newMerchant)
                                    field.onChange([...(field.value || []), newMerchant.value])
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.merchantName}</td>
                                <td className="px-2 py-1">{m.merchantCode}</td>
                                <td className="px-2 py-1">{m.externalId}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected merchants */}
                  {selectedMerchants.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Stores</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMerchants.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMerchants(item) // toggle remove
                              field.onChange(
                                (field.value || []).filter((id: string) => id !== item.value)
                              )
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </>
      )}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="imageUrl"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="imageUrl"
              label="Image URL"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="offerId"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="offerId"
              label="Offer ID"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
        <Controller
          control={form.control}
          name="voucherValue"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherValue"
              label="Voucher Value"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>
      <div className="flex justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoadingVoucherCreate}
        >
          <span className="tracking-wider font-bold">
            {isLoadingVoucherCreate ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>
    </form >
  )
}